import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pause, Play, SendHorizontal, Square, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";

interface MobileAudioRecorderProps {
  /** Recebe a URL pública do áudio já no storage (mesmo contrato do chat web) */
  onSendAudio: (audioUrl: string) => Promise<boolean>;
  /** Volta o composer ao estado normal */
  onClose: () => void;
}

/** O bucket chat-audios tem file_size_limit de 10MB */
const MAX_AUDIO_BYTES = 10 * 1024 * 1024;
const MAX_RECORDING_SECONDS = 5 * 60;

const MIME_CANDIDATES = [
  "audio/webm",
  "audio/webm;codecs=opus",
  "audio/ogg;codecs=opus",
  "audio/mp4",
  "audio/wav",
];

/**
 * A extensão do arquivo importa: a edge function send-audio deduz o mimetype
 * enviado ao WhatsApp a partir da extensão da URL. No iOS o MediaRecorder só
 * produz audio/mp4, então gravar tudo como .ogg entregaria o mimetype errado.
 */
const extensionForMime = (mimeType: string): string => {
  const base = mimeType.split(";")[0].trim();
  if (base === "audio/mp4" || base === "audio/aac") return "m4a";
  if (base === "audio/ogg") return "ogg";
  if (base === "audio/wav") return "wav";
  if (base === "audio/mpeg") return "mp3";
  return "webm";
};

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

export const MobileAudioRecorder = ({ onSendAudio, onClose }: MobileAudioRecorderProps) => {
  const { t } = useTranslation("chat");
  const [state, setState] = useState<"starting" | "recording" | "stopped">("starting");
  const [elapsed, setElapsed] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const previewUrlRef = useRef<string>("");
  const canceledRef = useRef(false);

  const [previewUrl, setPreviewUrl] = useState("");

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const revokePreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }
  };

  // Inicia a gravação assim que o componente monta (o usuário já tocou no mic)
  useEffect(() => {
    let disposed = false;
    // Reset explícito: em StrictMode o efeito monta duas vezes e a primeira
    // limpeza deixaria a flag ligada, engolindo a gravação seguinte.
    canceledRef.current = false;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        if (disposed) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        const selectedMimeType = MIME_CANDIDATES.find((mimeType) =>
          MediaRecorder.isTypeSupported(mimeType)
        );

        const mediaRecorder = new MediaRecorder(
          stream,
          selectedMimeType ? { mimeType: selectedMimeType } : undefined
        );

        chunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          stopStream();
          clearTimer();

          if (canceledRef.current) return;

          const blob = new Blob(chunksRef.current, {
            type: mediaRecorder.mimeType || "audio/webm",
          });

          if (blob.size === 0) {
            alert(t("input.errNoAudio"));
            onClose();
            return;
          }

          blobRef.current = blob;
          const url = URL.createObjectURL(blob);
          previewUrlRef.current = url;
          setPreviewUrl(url);
          setState("stopped");
        };

        mediaRecorder.onerror = () => {
          alert(t("input.errRecording"));
          stopStream();
          clearTimer();
          onClose();
        };

        mediaRecorder.start(100);
        mediaRecorderRef.current = mediaRecorder;
        setState("recording");
        setElapsed(0);

        timerRef.current = window.setInterval(() => {
          setElapsed((prev) => prev + 1);
        }, 1000);
      } catch (error) {
        const name = (error as DOMException)?.name;
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          alert(t("input.errMicDenied"));
        } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
          alert(t("input.errMicNotFound"));
        } else if (name === "NotReadableError" || name === "TrackStartError") {
          alert(t("input.errMicBusy"));
        } else {
          console.error("[MobileAudioRecorder] Erro ao iniciar gravação:", error);
          alert(t("input.errMicGeneric"));
        }
        onClose();
      }
    };

    start();

    return () => {
      disposed = true;
      canceledRef.current = true;
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      stopStream();
      clearTimer();
      revokePreview();
    };
    // Monta uma única vez: o ciclo de vida da gravação é o do componente.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStop = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  // Corta a gravação no limite: acima de ~10MB o upload no bucket falharia
  useEffect(() => {
    if (state !== "recording" || elapsed < MAX_RECORDING_SECONDS) return;
    handleStop();
    alert(t("input.errAudioTooLong"));
  }, [state, elapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDiscard = () => {
    canceledRef.current = true;
    audioElementRef.current?.pause();
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    stopStream();
    clearTimer();
    revokePreview();
    onClose();
  };

  const togglePlayPause = () => {
    const el = audioElementRef.current;
    if (!el) return;

    if (isPlaying) {
      el.pause();
      setIsPlaying(false);
    } else {
      el.play();
      setIsPlaying(true);
    }
  };

  const handleSend = async () => {
    const blob = blobRef.current;
    if (!blob) {
      alert(t("input.errNothingToSend"));
      return;
    }

    if (!supabase) {
      alert(t("input.errNotConfigured"));
      return;
    }

    if (blob.size > MAX_AUDIO_BYTES) {
      alert(t("input.errAudioTooBig"));
      return;
    }

    setIsSending(true);

    try {
      const contentType = (blob.type || "audio/webm").split(";")[0].trim();
      const filePath = `audios/audio_${Date.now()}.${extensionForMime(blob.type)}`;

      const { error: uploadError } = await supabase.storage
        .from("chat-audios")
        .upload(filePath, blob, { contentType, cacheControl: "3600", upsert: false });

      if (uploadError) {
        console.error("[MobileAudioRecorder] Erro no upload:", uploadError);
        alert(t("input.errUploadAudio"));
        return;
      }

      const { data: urlData } = supabase.storage.from("chat-audios").getPublicUrl(filePath);

      const success = await onSendAudio(urlData.publicUrl);
      if (!success) {
        alert(t("input.errSendAudio"));
        return;
      }

      canceledRef.current = true;
      revokePreview();
      onClose();
    } catch (error) {
      console.error("[MobileAudioRecorder] Erro ao enviar áudio:", error);
      alert(t("input.errProcessAudio"));
    } finally {
      setIsSending(false);
    }
  };

  const isPreview = state === "stopped";

  return (
    <div className="flex items-center gap-2">
      {isPreview && previewUrl && (
        <audio
          ref={audioElementRef}
          src={previewUrl}
          onEnded={() => setIsPlaying(false)}
          className="hidden"
        />
      )}

      <button
        type="button"
        onClick={handleDiscard}
        disabled={isSending}
        aria-label={t("input.discardAudioAria")}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-red-500 transition-colors active:bg-red-500/10 disabled:opacity-50"
      >
        <Trash2 className="h-5 w-5" />
      </button>

      <div className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-3xl border border-border bg-background px-3">
        {isPreview ? (
          <button
            type="button"
            onClick={togglePlayPause}
            disabled={isSending}
            aria-label={isPlaying ? t("input.pauseAudioAria") : t("input.playAudioAria")}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-foreground transition-colors active:bg-hover disabled:opacity-50"
          >
            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
        ) : (
          <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-red-500" />
        )}

        <span className="font-mono text-[14px] tabular-nums text-foreground">
          {formatTime(elapsed)}
        </span>
      </div>

      {isPreview ? (
        <button
          type="button"
          onClick={handleSend}
          disabled={isSending}
          aria-label={t("input.sendAudioAria")}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-blue text-white shadow-sm transition-all active:scale-95 disabled:opacity-40"
        >
          {isSending ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <SendHorizontal className="h-5 w-5" />
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleStop}
          disabled={state !== "recording"}
          aria-label={t("input.stopRecordingAria")}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-500 text-white shadow-sm transition-all active:scale-95 disabled:opacity-40"
        >
          <Square className="h-4 w-4 fill-current" />
        </button>
      )}
    </div>
  );
};
